import { Box, MenuItem } from "@material-ui/core";
import {
  CalculatorBodyText,
  CalculatorHeadline,
  CalculatorTopicH2Headline,
  PreCalculatorHeadline,
} from "src/components/Calculator/CalculatorTypography";
import React, { FC, useState } from "react";
import {
  calculateBar,
  calculatePis,
  createBarData,
  createPSIData,
} from "src/components/Calculator/psiBarCalculatorHelpers";

import { CalculatorButtonLinkCentered } from "src/components/Calculator/components/CalculatorButtonLinkCentered";
import { CalculatorImage } from "src/components/Calculator/CalculatorImage";
import { CalculatorInput } from "src/components/Calculator/CalculatorInput";
import { CalculatorLayout } from "src/components/Calculator/CalculatorLayout";
import { CalculatorPSIBARTable } from "src/components/Calculator/CalculatorPSIBARTable";
import CycleverseDivider from "src/components/CycleverseDivider";
import CycleverseSelect from "src/components/CycleverseSelect";
import SeoHead from "src/components/Seo/SeoHead";
import { TheCycleverseTheme } from "src/theme";
import { makeStyles } from "@material-ui/styles";
import useTranslation from "next-translate/useTranslation";

const useStyles = makeStyles((theme: TheCycleverseTheme) => ({
  formulaContainer: {
    [theme.breakpoints.down("sm")]: {
      minWidth: "100%",
      fontWeight: "bold",
      fontSize: "16px",
    },
    width: "60%",
    display: "flex",
    flex: "row",
    marginTop: "20px",
    flexWrap: "wrap",
    margin: "auto",
    justifyContent: "space-around",
  },
  heading: {
    lineHeight: "40px",
  },
  formulaValue: {
    [theme.breakpoints.down("sm")]: {
      minWidth: "20%",
      fontWeight: "bold",
      fontSize: "16px",
    },
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "white",
    minWidth: "20%",
    borderRadius: "5px",
    textAlign: "center",
    border: "none",
    fontWeight: "bold",
    fontSize: "16px",
    marginBottom: 0,
    marginTop: 0,
  },
  formulaInput: {
    display: "inline-block",
    minWidth: "20%",
    maxWidth: "40%",
    width: "10%",
    [theme.breakpoints.down("sm")]: {
      display: "inline-block",
      minWidth: "40px",
      maxWidth: "40%",
      width: "20%",
    },
  },
  formulaValueText: {
    fontWeight: "bold",
    fontSize: "16px",
    textAlign: "center",
  },
  formulaValueImageBetween: {
    display: "flex",
    alignItems: "center",
  },
  allTables: {
    display: "flex",
    width: "100%",
    justifyContent: "space-between",
    alignContent: "space-between",
    gap: "20px",
    [theme.breakpoints.down("sm")]: {
      margin: "0px",
      display: "flex",
      flexDirection: "column",
      width: "100%",
      alignItems: "center",
    },
  },
  headerStyles: {
    display: "flex",
    flexDirection: "column",
  },
  button: {
    backgroundColor: "#04B0E9",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#04B0E9",
    },
  },
  buttonContainer: {
    display: "flex",
    alignContent: "center",
    justifyContent: "center",
    marginTop: "25px",
  },
  link: {
    textDecoration: "underline",
    color: theme.openButtonColor,
  },
  notStyled: {
    textDecoration: "inherit",
    color: "inherit",
  },
  valueStyles: {
    color: theme.openButtonColor,
  },
  mainContainer: {
    maxWidth: "90vh",
    border: "1px solid red",
  },
}));

const PSIBarCalculator: FC = () => {
  const { t, lang } = useTranslation("psiBar");
  const classes = useStyles();
  const [unite, setUnite] = useState<any | null>("psi");
  const [value, setValue] = useState<any>("1");

  const handleOnChange = (e) => {
    const onlyNumbersRegexDE = /^[0-9]*([,][0-9]*)?$/;
    const onlyNumbersRegexEN = /^[0-9]*([.][0-9]*)?$/;
    if (lang === "de" && onlyNumbersRegexDE.test(e)) {
      setValue(e);
    }
    if (lang === "en" && onlyNumbersRegexEN.test(e)) {
      setValue(e);
    }
  };

  return (
    <CalculatorLayout
      header={
        <div className={classes.headerStyles} data-test="page-rechner-psi-bar-rechner" id="top">
          <PreCalculatorHeadline>{t("barToPsiHeadLine")}</PreCalculatorHeadline>
          <CalculatorHeadline>
            <span className={classes.heading}>{t("barToPsiTitle")}</span>
          </CalculatorHeadline>
        </div>
      }
    >
      <SeoHead
        title={t("barPsiSeoTitle")}
        description={t("barPsiSeoDescription")}
        ogImage="/images/The-Cycleverse-OpenGraph-Bar-PSI-Rechner.png"
        ogDescription={t("openGraphDescription")}
        ogTitle={t("psiOpenGraphTitel")}
      />
      <CalculatorImage
        src="/images/calculators/bar-psi-rechner.svg"
        width={200}
        height={200}
        layout="intrinsic"
        alt={t("barPsiText15")}
        id="calculator"
      />
      <div className={classes.formulaContainer}>
        <div className={`${classes.formulaValue} ${classes.formulaInput}`}>
          <CalculatorInput
            type="text"
            placeholder="1"
            value={value}
            onChange={(e) => handleOnChange(e?.target?.value)}
          />
        </div>

        <div className={classes.formulaValue}>
          <Box display="flex" justifyContent="center">
            <CycleverseSelect
              value={unite}
              onChange={(e) => {
                setUnite(e?.target?.value);
              }}
            >
              {["psi", "bar"].map((key) => (
                <MenuItem key={key} value={key}>
                  {key}
                </MenuItem>
              ))}
            </CycleverseSelect>
          </Box>
        </div>
        <div className={classes.formulaValueImageBetween}>
          <CalculatorImage
            src="/images/calculators/bar-psi-pfeil.svg"
            width={40}
            height={40}
            layout="intrinsic"
            alt="Kettenlänge berechnen"
          />
        </div>
        <div className={`${classes.formulaValue}`}>
          <span className={`${classes.formulaValueText} ${classes.valueStyles} `}>
            {unite !== "psi"
              ? calculatePis(value?.replace(",", "."), 4, lang)
              : calculateBar(value?.replace(",", "."), 4, lang)}
          </span>
        </div>
        <div className={classes.formulaValue}>
          <span className={classes.formulaValueText}>{unite === "bar" ? "psi" : "bar"}</span>
        </div>
      </div>

      <CalculatorBodyText>{t("barPsiText1")}</CalculatorBodyText>

      <CycleverseDivider marginTop={4} />
      <CalculatorTopicH2Headline>{t("barPsiText2")}</CalculatorTopicH2Headline>
      <CalculatorBodyText>{t("barPsiText3")}</CalculatorBodyText>
      <CalculatorBodyText>{t("barPsiText4")}</CalculatorBodyText>
      <CalculatorBodyText>{t("barPsiText5")}</CalculatorBodyText>
      <CalculatorBodyText>{t("barPsiText6")}</CalculatorBodyText>

      <CalculatorTopicH2Headline>{t("barPsiText7")}</CalculatorTopicH2Headline>
      <CalculatorBodyText>{t("barPsiText8")}</CalculatorBodyText>
      <CalculatorBodyText>{t("barPsiText9")}</CalculatorBodyText>
      <CalculatorBodyText>{t("barPsiText10")}</CalculatorBodyText>
      <CalculatorBodyText>1 bar ≈ 14,504 psi 1 psi ≈ 0,069 bar</CalculatorBodyText>
      <CalculatorBodyText>
        {t("barPsiText11")}{" "}
        <a href="#calculator" className={classes.link}>
          {t("barPsiText12")}
        </a>
        .{" "}
      </CalculatorBodyText>
      <CalculatorBodyText>{t("barPsiText13")}</CalculatorBodyText>
      <div className={classes.allTables}>
        <CalculatorPSIBARTable title1="psi" title2="bar" data={createPSIData()} />
        <CalculatorPSIBARTable title1="bar" title2="psi" data={createBarData()} />
      </div>
      <CalculatorButtonLinkCentered href="/pumpen" text={t("barPsiText14")} />
    </CalculatorLayout>
  );
};

export default PSIBarCalculator;
